// src/components/PitchMap.jsx
import React from 'react';
import { 
  Box, 
  Paper, 
  Typography,
  useTheme 
} from '@mui/material';

const PitchMap = ({ pitchImpact, size = 400 }) => {
  const theme = useTheme();
  
  // Cricket pitch dimensions: 20.12m x 3.05m
  const pitchWidth = 3.05;
  const pitchLength = 20.12;
  
  // Scale factor for visualization
  const scaleX = size / pitchLength;
  const scaleY = (size * 0.15) / pitchWidth; // Make pitch narrower for visualization
  
  // Calculate position (center y = 0 in data, but middle of visualization)
  const x = pitchImpact.x * scaleX;
  const y = (pitchImpact.y + pitchWidth/2) * scaleY;
  
  // Determine color based on length category
  const getLengthColor = (category) => {
    switch(category?.toLowerCase()) {
      case 'yorker':
      case 'full_toss':
        return theme.palette.error.main;
      case 'good':
        return theme.palette.success.main;
      case 'short':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Pitch Impact Map
      </Typography>
      
      <Box sx={{ 
        position: 'relative', 
        width: size, 
        height: size * 0.2,
        backgroundColor: '#4CAF50', // Green pitch color
        border: `3px solid ${theme.palette.common.white}`,
        borderRadius: 2,
        overflow: 'hidden',
        mx: 'auto',
        mt: 2
      }}>
        {/* Pitch markings */}
        {/* Center line */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 2,
          height: '100%',
          backgroundColor: theme.palette.common.white,
          opacity: 0.5
        }} />
        
        {/* Crease lines */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: '20%',
          width: '60%',
          height: 2,
          backgroundColor: theme.palette.common.white
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: '20%',
          width: '60%',
          height: 2,
          backgroundColor: theme.palette.common.white
        }} />
        
        {/* Stumps (both ends) */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '10%',
          transform: 'translate(-50%, -50%)',
          width: 4,
          height: '60%',
          backgroundColor: '#795548'
        }} />
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '90%',
          transform: 'translate(-50%, -50%)',
          width: 4,
          height: '60%',
          backgroundColor: '#795548'
        }} />
        
        {/* Impact point */}
        <Box
          sx={{
            position: 'absolute',
            left: `${x}px`,
            top: `${y}px`,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: getLengthColor(pitchImpact.length_category),
            border: `3px solid ${theme.palette.common.white}`,
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 0 10px rgba(0,0,0,0.5)',
            zIndex: 10
          }}
        />
        
        {/* Bounce arc (if bounce height available) */}
        {pitchImpact.bounce_height && (
          <Box
            sx={{
              position: 'absolute',
              left: `${x}px`,
              top: `${y}px`,
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: `2px dashed ${theme.palette.warning.main}`,
              transform: 'translate(-50%, -50%)',
              animation: 'pulse 2s infinite'
            }}
          />
        )}
      </Box>
      
      {/* Statistics */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Length
            </Typography>
            <Typography variant="body1" fontWeight="bold" color={getLengthColor(pitchImpact.length_category)}>
              {pitchImpact.length_category.replace('_', ' ').toUpperCase()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Line
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {pitchImpact.line_category.toUpperCase()}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ 
          backgroundColor: theme.palette.background.default,
          p: 2,
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Coordinates:</strong> {pitchImpact.x.toFixed(2)}m from crease, {pitchImpact.y.toFixed(2)}m from center
          </Typography>
          {pitchImpact.bounce_height && (
            <Typography variant="body2" color="text.secondary">
              <strong>Bounce Height:</strong> {pitchImpact.bounce_height.toFixed(2)}m
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default PitchMap;